import React, { useEffect, useState } from 'react';
import { Calendar, Check, X, AlertCircle, FileText, Users, Cake, Gift, Clock, CalendarDays, RefreshCw } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../services/api';
import { toast } from 'sonner';

const CoachDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [classList, setClassList] = useState([]);
  const [myClients, setMyClients] = useState([]);
  const [birthdays, setBirthdays] = useState({ today: [], upcoming: [] });
  const [coachName, setCoachName] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [note, setNote] = useState('');
  const [milestone, setMilestone] = useState('');
  const [rescheduleClient, setRescheduleClient] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');

  useEffect(() => {
    fetchMyClients();
    fetchBirthdays();
  }, []);

  useEffect(() => {
    fetchClassList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchMyClients = async () => {
    try {
      const response = await api.get('/coach/my-clients');
      setMyClients(response.data.clients || []);
      setCoachName(response.data.coach_name);
    } catch (error) {
      console.error('Error fetching my clients:', error);
      setMyClients([]);
    }
  };

  const fetchBirthdays = async () => {
    try {
      const response = await api.get('/coach/my-birthdays');
      setBirthdays(response.data || { today: [], upcoming: [] });
    } catch (error) {
      console.error('Error fetching birthdays:', error);
      setBirthdays({ today: [], upcoming: [] });
    }
  };

  const fetchClassList = async () => {
    try {
      const response = await api.get(`/coach/daily-class?date=${selectedDate}`);
      setClassList(response.data.clients);
    } catch (error) {
      console.error('Error fetching class list:', error);
      toast.error('Failed to load class list');
    }
  };

  const markAttendance = async (clientId, status, clientName) => {
    setLoading(true);
    try {
      await api.post('/attendance/mark', {
        client_id: clientId,
        client_name: clientName,
        session_date: selectedDate,
        session_time: new Date().toLocaleTimeString(),
        status,
        coach_name: coachName || user?.full_name || 'Coach',
        marked_by: user?.email || 'coach@streamlineaquatics.co',
        notes: ''
      });
      
      toast.success(`Attendance marked: ${status}`);
      fetchClassList();
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error('Failed to mark attendance');
    } finally {
      setLoading(false);
    }
  };

  const addNote = async () => {
    if (!selectedClient || !note) return;
    
    try {
      await api.post('/coach/notes', {
        client_id: selectedClient.client_id,
        note,
        milestone: milestone || null
      });
      
      toast.success('Note added successfully');
      setNote('');
      setMilestone('');
      setSelectedClient(null);
      // Refresh data to show updated notes
      fetchMyClients();
      fetchClassList();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleClient || !rescheduleDate) {
      toast.error('Please select a new date');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/coach/reschedule', {
        client_id: rescheduleClient.client_id,
        original_date: selectedDate,
        new_date: rescheduleDate,
        reason: rescheduleReason || 'Client requested reschedule'
      });
      
      toast.success(`Session rescheduled to ${rescheduleDate}`);
      setRescheduleClient(null);
      setRescheduleDate('');
      setRescheduleReason('');
      fetchClassList();
    } catch (error) {
      console.error('Error rescheduling:', error);
      toast.error('Failed to reschedule session');
    } finally {
      setLoading(false);
    }
  };

  // Get upcoming 7 days of lessons
  const getUpcomingDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Coach Dashboard</h1>
            <p className="text-lg text-slate-600">Welcome, {coachName || 'Coach'}! Manage your clients and track attendance.</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-cyan-50 to-white border-cyan-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">My Clients</p>
                    <p className="text-3xl font-bold text-cyan-700">{myClients.length}</p>
                  </div>
                  <Users className="w-10 h-10 text-cyan-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Today's Lessons</p>
                    <p className="text-3xl font-bold text-green-700">{classList.length}</p>
                  </div>
                  <CalendarDays className="w-10 h-10 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Birthdays Today</p>
                    <p className="text-3xl font-bold text-amber-700">{birthdays.today?.length || 0}</p>
                  </div>
                  <Gift className="w-10 h-10 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Upcoming Birthdays</p>
                    <p className="text-3xl font-bold text-purple-700">{birthdays.upcoming?.length || 0}</p>
                  </div>
                  <Cake className="w-10 h-10 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="schedule" data-testid="tab-schedule">
                <CalendarDays className="w-4 h-4 mr-2" />
                Upcoming Lessons
              </TabsTrigger>
              <TabsTrigger value="attendance" data-testid="tab-attendance">
                <Calendar className="w-4 h-4 mr-2" />
                Attendance
              </TabsTrigger>
              <TabsTrigger value="clients" data-testid="tab-clients">
                <Users className="w-4 h-4 mr-2" />
                My Clients
              </TabsTrigger>
              <TabsTrigger value="birthdays" data-testid="tab-birthdays">
                <Cake className="w-4 h-4 mr-2" />
                Birthdays
              </TabsTrigger>
            </TabsList>

            {/* Upcoming Lessons / Schedule Tab */}
            <TabsContent value="schedule">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center">
                      <CalendarDays className="w-5 h-5 mr-2" />
                      Upcoming Lessons (Next 7 Days)
                    </span>
                    <Button variant="outline" size="sm" onClick={() => { fetchMyClients(); fetchClassList(); }}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myClients.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p>No clients assigned to you yet</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* List clients with their scheduled sessions */}
                      {myClients.map((client) => (
                        <div
                          key={client.client_id}
                          className="p-5 bg-white border-2 border-slate-100 rounded-xl hover:border-cyan-200 transition-all"
                          data-testid={`schedule-client-${client.client_id}`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center space-x-3 mb-1">
                                <h3 className="text-lg font-semibold text-slate-900">{client.full_name}</h3>
                                <Badge variant="outline">{client.client_id}</Badge>
                                <Badge className={
                                  client.program_type === 'marlins' ? 'bg-orange-100 text-orange-700' :
                                  client.program_type === 'adults' ? 'bg-blue-100 text-blue-700' :
                                  'bg-cyan-100 text-cyan-700'
                                }>
                                  {client.program_type}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600">
                                <Clock className="w-4 h-4 inline mr-1" />
                                Schedule: {client.assigned_schedule || 'Not specified'}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className={`text-2xl font-bold ${client.sessions_remaining <= 3 ? 'text-red-600' : 'text-green-600'}`}>
                                {client.sessions_remaining}
                              </div>
                              <p className="text-xs text-slate-500">sessions left</p>
                            </div>
                          </div>
                          
                          {/* Contact Info */}
                          <div className="flex items-center space-x-4 text-sm text-slate-600 mb-3">
                            <span>📧 {client.email}</span>
                            <span>📱 {client.phone}</span>
                          </div>
                          
                          {/* Previous Notes Section */}
                          {client.notes && client.notes.length > 0 && (
                            <div className="bg-slate-50 rounded-lg p-3 mb-3">
                              <p className="text-xs font-semibold text-slate-600 mb-2">📝 Previous Session Notes:</p>
                              <div className="space-y-2 max-h-32 overflow-y-auto">
                                {client.notes.slice(-3).reverse().map((n, idx) => (
                                  <div key={idx} className="text-sm border-l-2 border-cyan-300 pl-2">
                                    <p className="text-slate-700">{n.note}</p>
                                    {n.milestone && (
                                      <p className="text-cyan-600 text-xs">🏆 {n.milestone}</p>
                                    )}
                                    <p className="text-xs text-slate-400">
                                      {n.date ? new Date(n.date).toLocaleDateString() : ''} by {n.coach || 'Coach'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2 pt-2 border-t border-slate-100">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedClient(client)}
                              data-testid={`schedule-add-note-${client.client_id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Add Note
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={() => {
                                setRescheduleClient(client);
                                setRescheduleDate('');
                                setRescheduleReason('');
                              }}
                              data-testid={`schedule-reschedule-${client.client_id}`}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reschedule
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Attendance Tab */}
            <TabsContent value="attendance">
              {/* Date Selector */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Select Date
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="max-w-xs"
                    data-testid="date-selector"
                  />
                </CardContent>
              </Card>

              {/* Class List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      Class List - {selectedDate}
                    </span>
                    <Badge variant="outline">{classList.length} Students</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {classList.map((client) => (
                      <div
                        key={client.client_id}
                        className="flex items-center justify-between p-4 bg-white border-2 border-slate-100 rounded-xl hover:border-cyan-200 transition-all"
                        data-testid={`client-${client.client_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-lg font-semibold text-slate-900">{client.full_name}</h3>
                            <Badge variant="outline">{client.client_id}</Badge>
                            {client.attendance_status === 'attended' && (
                              <Badge className="bg-green-100 text-green-700">Present</Badge>
                            )}
                            {client.attendance_status === 'missed' && (
                              <Badge variant="destructive">Absent</Badge>
                            )}
                            {client.attendance_status === 'excused' && (
                              <Badge className="bg-yellow-100 text-yellow-700">Excused</Badge>
                            )}
                          </div>
                          <div className="mt-2 text-sm text-slate-600">
                            <span className="mr-4">📧 {client.email}</span>
                            <span className="mr-4">📱 {client.phone}</span>
                            <span>Sessions Remaining: <strong>{client.sessions_remaining}</strong></span>
                          </div>
                          {client.notes && client.notes.length > 0 && (
                            <div className="mt-2 text-sm text-slate-500">
                              Last Note: {client.notes[client.notes.length - 1].note}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          {client.attendance_status === 'pending' && (
                            <>
                              <Button
                                onClick={() => markAttendance(client.client_id, 'attended', client.full_name)}
                                className="bg-green-600 hover:bg-green-700"
                                disabled={loading}
                                data-testid={`mark-present-${client.client_id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Present
                              </Button>
                              <Button
                                onClick={() => markAttendance(client.client_id, 'missed', client.full_name)}
                                variant="destructive"
                                disabled={loading}
                                data-testid={`mark-absent-${client.client_id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Absent
                              </Button>
                              <Button
                                onClick={() => markAttendance(client.client_id, 'excused', client.full_name)}
                                variant="outline"
                                disabled={loading}
                                data-testid={`mark-excused-${client.client_id}`}
                              >
                                <AlertCircle className="w-4 h-4 mr-1" />
                                Excused
                              </Button>
                            </>
                          )}
                          <Button
                            onClick={() => setSelectedClient(client)}
                            variant="outline"
                            data-testid={`add-note-${client.client_id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {classList.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        No students scheduled for this date
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* My Clients Tab */}
            <TabsContent value="clients">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      My Assigned Clients
                    </span>
                    <Badge variant="outline">{myClients.length} Total</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myClients.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      No clients assigned to you yet
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {myClients.map((client) => (
                        <div
                          key={client.client_id}
                          className="p-4 bg-white border-2 border-slate-100 rounded-xl hover:border-cyan-200 transition-all"
                          data-testid={`my-client-${client.client_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="text-lg font-semibold text-slate-900">{client.full_name}</h3>
                                <Badge variant="outline">{client.client_id}</Badge>
                                <Badge className={
                                  client.program_type === 'marlins' ? 'bg-orange-100 text-orange-700' :
                                  client.program_type === 'adults' ? 'bg-blue-100 text-blue-700' :
                                  'bg-cyan-100 text-cyan-700'
                                }>
                                  {client.program_type}
                                </Badge>
                              </div>
                              <div className="text-sm text-slate-600 space-y-1">
                                <p>📧 {client.email}</p>
                                <p>📱 {client.phone}</p>
                                {client.assigned_schedule && (
                                  <p>📅 Schedule: {client.assigned_schedule}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-2xl font-bold ${client.sessions_remaining <= 3 ? 'text-red-600' : 'text-green-600'}`}>
                                {client.sessions_remaining}
                              </div>
                              <p className="text-xs text-slate-500">sessions left</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {client.sessions_attended || 0} attended
                              </p>
                            </div>
                          </div>
                          {client.notes && client.notes.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs font-semibold text-slate-500 mb-1">Latest Note:</p>
                              <p className="text-sm text-slate-600">{client.notes[client.notes.length - 1].note}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Birthdays Tab */}
            <TabsContent value="birthdays">
              <div className="space-y-6">
                {/* Today's Birthdays */}
                {birthdays.today?.length > 0 && (
                  <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white">
                    <CardHeader>
                      <CardTitle className="flex items-center text-amber-700">
                        <Gift className="w-5 h-5 mr-2" />
                        🎉 Today's Birthdays!
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {birthdays.today.map((client) => (
                          <div
                            key={client.client_id}
                            className="flex items-center justify-between p-4 bg-white rounded-xl border border-amber-200"
                            data-testid={`birthday-today-${client.client_id}`}
                          >
                            <div className="flex items-center space-x-4">
                              <div className="text-4xl">🎂</div>
                              <div>
                                <h3 className="font-semibold text-slate-900">{client.full_name}</h3>
                                <p className="text-sm text-slate-600">Turning {client.age} today!</p>
                                <Badge variant="outline" className="mt-1">{client.program_type}</Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Upcoming Birthdays */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center">
                        <Cake className="w-5 h-5 mr-2" />
                        Upcoming Birthdays (Next 30 Days)
                      </span>
                      <Badge variant="outline">{birthdays.upcoming?.length || 0} upcoming</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(!birthdays.upcoming || birthdays.upcoming.length === 0) ? (
                      <div className="text-center py-12 text-slate-500">
                        <Cake className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <p>No upcoming birthdays in the next 30 days</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {birthdays.upcoming.map((client) => (
                          <div
                            key={client.client_id}
                            className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all"
                            data-testid={`birthday-upcoming-${client.client_id}`}
                          >
                            <div className="flex items-center space-x-4">
                              <div className="text-2xl">🎈</div>
                              <div>
                                <h3 className="font-semibold text-slate-900">{client.full_name}</h3>
                                <p className="text-sm text-slate-600">
                                  {client.date_of_birth} (turning {client.age + 1})
                                </p>
                                <Badge variant="outline" className="mt-1">{client.program_type}</Badge>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-lg font-bold ${client.days_until <= 7 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {client.days_until} days
                              </div>
                              <p className="text-xs text-slate-500">until birthday</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Add Note Modal */}
          {selectedClient && (
            <Card className="fixed inset-0 m-auto max-w-2xl h-fit z-50 shadow-2xl bg-white border-2 border-slate-200">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle>Add Note for {selectedClient.full_name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6 bg-white">
                <div>
                  <Label htmlFor="note">Progress Note</Label>
                  <Textarea
                    id="note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    placeholder="e.g., Improved breathing technique today..."
                    className="bg-white border-2"
                    data-testid="note-input"
                  />
                </div>

                <div>
                  <Label htmlFor="milestone">Milestone (Optional)</Label>
                  <Input
                    id="milestone"
                    value={milestone}
                    onChange={(e) => setMilestone(e.target.value)}
                    placeholder="e.g., Can float independently"
                    className="bg-white border-2"
                    data-testid="milestone-input"
                  />
                </div>

                {/* Show existing notes for this client */}
                {selectedClient.notes && selectedClient.notes.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <Label className="text-slate-700 font-semibold">Previous Notes:</Label>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-2">
                      {selectedClient.notes.slice(-5).reverse().map((n, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg border text-sm">
                          <p className="text-slate-700">{n.note}</p>
                          {n.milestone && (
                            <p className="text-cyan-600 mt-1 text-xs">🏆 Milestone: {n.milestone}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {n.date ? new Date(n.date).toLocaleDateString() : 'Date not recorded'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-2">
                  <Button onClick={addNote} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="save-note-btn">
                    Save Note
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedClient(null);
                      setNote('');
                      setMilestone('');
                    }}
                    variant="outline"
                    className="flex-1"
                    data-testid="cancel-note-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Overlay for modal */}
          {selectedClient && (
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setSelectedClient(null)}
            />
          )}

          {/* Reschedule Modal */}
          {rescheduleClient && (
            <Card className="fixed inset-0 m-auto max-w-lg h-fit z-50 shadow-2xl bg-white border-2 border-slate-200">
              <CardHeader className="bg-amber-50 border-b">
                <CardTitle className="text-amber-700">Reschedule Session</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6 bg-white">
                <p className="text-slate-600">
                  Rescheduling session for <strong>{rescheduleClient.full_name}</strong>
                </p>
                
                <div>
                  <Label htmlFor="newDate">New Session Date *</Label>
                  <Input
                    id="newDate"
                    type="date"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-white border-2"
                    data-testid="reschedule-date"
                  />
                </div>
                
                <div>
                  <Label htmlFor="reason">Reason (Optional)</Label>
                  <Textarea
                    id="reason"
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    placeholder="e.g., Client travel, illness, etc."
                    rows={2}
                    className="bg-white border-2"
                    data-testid="reschedule-reason"
                  />
                </div>
                
                <div className="flex space-x-3 pt-2">
                  <Button 
                    onClick={handleReschedule} 
                    disabled={loading || !rescheduleDate}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    data-testid="confirm-reschedule-btn"
                  >
                    {loading ? 'Saving...' : 'Confirm Reschedule'}
                  </Button>
                  <Button
                    onClick={() => {
                      setRescheduleClient(null);
                      setRescheduleDate('');
                      setRescheduleReason('');
                    }}
                    variant="outline"
                    className="flex-1"
                    data-testid="cancel-reschedule-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reschedule Overlay */}
          {rescheduleClient && (
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setRescheduleClient(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachDashboard;
